import {
  genericGetSingle,
  genericGetAll,
  makeAPIRequest,
} from "../helpers/apiCall";
import { Material } from "../models";

/**
 * Get all materials
 * @param params URLSearchParams
 * @returns Promise<{ materials: Material[] }>
 */
function getAll(
  params: URLSearchParams = null,
): Promise<{ materials: Material[] }> {
  return genericGetAll("materials", params);
}

/**
 * Get a single material
 * @param id string
 * @returns Promise<{ material: Material }>
 */
function getSingle(id: string): Promise<{ material: Material }> {
  return genericGetSingle("materials", id);
}

/**
 * Gets the count of materials
 * @param params URLSearchParams
 * @returns Promise<{ count: number }>
 */
function getCount(params: URLSearchParams = null): Promise<{ count: number }> {
  if (!params) params = new URLSearchParams();
  params.append("countOnly", "true");
  return genericGetAll("materials", params);
}

/**
 * Retrieves the warehouse breakdown for a given material.
 * @param {string} id - The ID of the material.
 * @returns {Promise<
 *   {
 *     id: string;
 *     currentQuantity: number;
 *     maxQuantity: number;
 *     warehouseName: string;
 *   }[]
 * >} Promise resolving to an array of warehouse breakdown objects.
 */
function getMaterialWarehouseBreakdown(id: string): Promise<
  {
    id: string;
    currentQuantity: number;
    maxQuantity: number;
    warehouseName: string;
  }[]
> {
  return makeAPIRequest({
    url: `/materials/${id}/breakdown/warehouses`,
    method: "get",
  })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Retrieves the breakdown of storage units for a given material.
 * @param id The ID of the material to retrieve storage unit breakdown for.
 * @returns A promise that resolves to an array of storage unit breakdown objects, each containing id, name, currentQuantity, maxQuantity, and warehouseName.
 */
function getMaterialStorageUnitBreakdown(id: string): Promise<
  {
    id: string;
    name: string;
    currentQuantity: number;
    maxQuantity: number;
    warehouseName: string;
  }[]
> {
  return makeAPIRequest({
    url: `/materials/${id}/breakdown/storage_units`,
    method: "get",
  })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Retrieves the breakdown of carriers for a given material ID.
 *
 * @param {string} id - The ID of the material to fetch carrier breakdown for.
 * @returns {Promise<
 *   {
 *     id: string;
 *     name: string;
 *     fullName: string;
 *     storageUnitName: string;
 *     warehouseName: string;
 *     currentQuantity: number;
 *     maxQuantity: number;
 *   }[]
 * >} A promise that resolves to an array of carrier breakdown objects.
 */
function getMaterialCarrierBreakdown(id: string): Promise<
  {
    id: string;
    name: string;
    fullName: string;
    storageUnitName: string;
    warehouseName: string;
    currentQuantity: number;
    maxQuantity: number;
  }[]
> {
  return makeAPIRequest({
    url: `/materials/${id}/breakdown/carriers`,
    method: "get",
  })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Retrieves the breakdown of material shelves for a given material ID.
 *
 * @param {string} id - The unique identifier of the material.
 * @returns {Promise<{ id: string; name: string; fullName: string; carrierName: string; storageUnitName: string; warehouseName: string; currentQuantity: number; maxQuantity: number; }[]>} A promise that resolves to an array of material shelf breakdown objects.
 */
function getMaterialShelvesBreakdown(id: string): Promise<
  {
    id: string;
    name: string;
    fullName: string;
    carrierName: string;
    storageUnitName: string;
    warehouseName: string;
    currentQuantity: number;
    maxQuantity: number;
  }[]
> {
  return makeAPIRequest({
    url: `/materials/${id}/breakdown/shelves`,
    method: "get",
  })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

export const materialService = {
  getAll,
  getSingle,
  getCount,
  getMaterialWarehouseBreakdown,
  getMaterialStorageUnitBreakdown,
  getMaterialCarrierBreakdown,
  getMaterialShelvesBreakdown,
};
